



































import { Component, Prop } from 'vue-property-decorator';
import videojs from 'video.js';
import strings from '@/strings.json';

import BaseComponent from './BaseComponent.vue';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';
import { UserConfModule } from '@/store/modules/userConf';
import { AppModule } from '@/store/modules/app';
import { ThemeConfModule } from '@/store/modules/theme';
import { ThemeConf } from '@/types/ThemeConf';
import { SKIP_CONST } from '@/constants';
import { BTBA, ChangeVideoSettingProperties, FinishedVideoProperties } from '@/analytics/events/BTBA';
import Timer from 'easytimer.js';

const UserConfState = getModule(UserConfModule, store);
const AppState = getModule(AppModule, store);
const Theme = getModule(ThemeConfModule, store);

interface avaliableTranscriptions {
  [key: string]: any;
}

interface ControlBarOptions extends videojs.ControlBarOptions {
  pictureInPictureToggle?: false | undefined;
}

@Component
export default class VideoPlayer extends BaseComponent {
  public $refs!: {
    videoPlayer: HTMLVideoElement;
    scrollList: HTMLElement;
    videoStage: HTMLElement;
    closeVideo: HTMLElement;
    buttonNext: HTMLElement;
  };
  private avaliableTranscriptions: avaliableTranscriptions = {};
  private transcriptions: object[] = [];
  private currentTranscription: string = '';
  private currentTime: number = 0;
  private autoscroll: boolean = true;
  private player!: videojs.Player;
  private textTrackSettings!: videojs.TextTrackSettings;
  private textTrackSettingsOptions!: videojs.TextTrackSettingsOptions;
  private stageHeight!: number;
  private hasCompleted: boolean = false;
  private loadTranscript: boolean = true;
  private showTranscriptOptions: boolean = false;
  @Prop() options!: object;
  @Prop() disableAutoplay?: boolean;
  @Prop() backgroundTheme!: string;
  @Prop({ default: true }) locksSlide!: boolean;
  @Prop({ default: true }) saveVideoData!: boolean;
  lastSavedTime: number = 0;
  completeSaved: boolean = false;
  skipMode: boolean = true;
  themeConfig: ThemeConf | null = null;
  duration: number = 0;
  isInsideModal: boolean = false;
  finished: boolean = false;
  sessionTimer : Timer = new Timer();

  unwatchGetAudio: any;
  unwatchHasStarted: any;

  mounted() {
    this.themeConfig = Theme.getTheme;
    this.skipMode = Theme.skipMode === SKIP_CONST.VIDEO_SKIP;

    if (this.locksSlide && !this.isCompleted()) this.lockForward();
    else this.hasCompleted = true;

    this.autoscroll = UserConfState.getAutomaticScroll;

    this.isInsideModal = this.$parent.$el.className === 'video-modal-container';

    this.unwatchGetAudio = store.watch(
      () => UserConfState.getAudio,
      () => {
        this.playAndPause();
      }
    );

    this.unwatchHasStarted = store.watch(
      () => AppState.hasStarted,
      () => {
        this.playIfPaused();
      }
    );

    if (this.$refs.videoPlayer) {
      let lang = 'custom';
      videojs.addLanguage(lang, strings);
      this.player = videojs(
        this.$refs.videoPlayer,
        {
          autoplay: this.disableAutoplay ? false : AppState.hasStarted,
          playbackRates: UserConfState.playbackRates,
          html5: { nativeTextTracks: AppState.getIsMobile, vhs: { overrideNative: true } },
          language: lang,
          controlBar: {
            pictureInPictureToggle: false,
            progressControl: this.skipMode
          } as ControlBarOptions,
          ...this.options,
          tracks: [],
          plugins: {
            reloadSourceOnError: {
              errorInterval: 10
            }
          }
        },
        this.playerReady
      );
      // SAVING TEXT-TRACK STATES
      this.textTrackSettings = this.player.getChild(
        'textTrackSettings'
      ) as videojs.TextTrackSettings;
    }

    let tryAgainButton: HTMLButtonElement | null = document.querySelector('button.try-again');
    if(tryAgainButton)
      this.$refs.buttonNext.textContent = tryAgainButton.textContent;
  }

  beforeDestroy() {
    AppState.setHasNeverChangedCaptionsSettings(false);
    this.unwatchGetAudio();
    this.unwatchHasStarted();
    if (this.player) 
      this.player.dispose();
    
  }

  replay() {
    this.emitRefreshed();
    this.player.play();
  }

  goNext(){
    // Emulating clickbutton to avoid breaking any existing logic baked in the navigation buttons
    let nextButton: HTMLButtonElement | null = document.querySelector('button.next.level-right');
    let tryAgainButton: HTMLButtonElement | null = document.querySelector('button.try-again');
    if(tryAgainButton)
      tryAgainButton.click();
    else if(nextButton)
      nextButton.click();
      
  }
  closeParentModal(){
    if(this.isInsideModal){
      let close = document.getElementById('modalCloseButton');
      if(close)
        close.click();
      
    }
  }
  get getBackgroundTheme(): string {
    return this.backgroundTheme;
  }

  playIfPaused() {
    if (AppState.hasStarted && !this.isCompleted() && this.player) this.player.play()?.catch(() => {});
  }

  isCompleted() {
    return this.getSavedData && this.getSavedData.completed;
  }

  handleClosedCaptionsChange(
    e: Event,
    data?: any,
    tracks?: TextTrackList
  ) {
    if (!tracks) tracks = this.player.textTracks();
    let firstCaption = this.getFirstCaption(tracks);

    let hasChanged = false;
    if (firstCaption){
      hasChanged = UserConfState.getShowCaptionsMode !== (firstCaption.mode);
      UserConfState.changeShowCaption(firstCaption.mode === 'showing');
    }

    if(hasChanged)
      setTimeout(()=>{
        this.$analytics.trackEvent(BTBA.changeVideoSetting,{
          'Video SRC' : `${this.player.src()}`,
          'Video Duration' : this.duration,
          'Changed Option' : 'caption',
          'Selected Value' : this.currentCaption
        } as ChangeVideoSettingProperties);
      },100);
  }

  getFirstCaption(tracks?: TextTrackList): TextTrack | undefined{
    if(tracks)
      for (let index = 0; index < tracks.length; index++) {
        const element = tracks[index];
        if(element.kind === 'captions')
          return element;
      }
    return undefined;
  }

  isCaptionActive(item: any) {
    return this.currentTime >= item.start && this.currentTime <= item.end;
  }

  handleCaptionSettingsMenu() {
    this.textTrackSettingsOptions = this.textTrackSettings.getValues();
    UserConfState.changeTextTrackSettingsOptions(this.textTrackSettingsOptions);
  }

  setTextTrackSettings(textTrackSettings: any) {
    if (textTrackSettings) this.textTrackSettings.setValues(textTrackSettings);
  }

  playAndPause() {
    if (this.player && this.player.el() && this.player.muted() !== !UserConfState.getAudio)
      this.player.muted(!UserConfState.getAudio);
  }

  playerReady(): void {
    this.$emit('player-ready',this.player );
    if(!this.skipMode)
      this.player.el().addEventListener('contextmenu', (e)=>e.preventDefault());
    this.player.on('timeupdate', this.handleTimeUpdate);
    this.player.on('volumechange', this.handlePlayerMuted);
    this.player.on('ratechange', this.handlePlaybackChange);
    this.player.on('play', this.handleVideoPlay);
    this.playAndPause();
    this.parseTextTracks((this.options as any).tracks);
    this.handleSavedData(this.getSavedData);

    this.player.volume(UserConfState.getVolume);
    this.unMuteVideoIfUserHasNotInteracted(
      this.player.muted(),
      UserConfState.getAudio
    );

    this.forceTrackLoad(this.player.textTracks(),AppState.hasNeverChangedCaptionsSettings);
    setTimeout(() => {
      this.forceTrackLoad(this.player.textTracks(),AppState.hasNeverChangedCaptionsSettings);
    }, 1000);

    this.player.defaultPlaybackRate(UserConfState.getSpeedRatio);
    this.player.playbackRate(UserConfState.getSpeedRatio);

    let elsPolite: NodeListOf<Element> | null = document.querySelectorAll(
      '.vjs-playback-rate button'
    );
    let el = elsPolite.item(0);
    if (el) 
      el.addEventListener('click', e => {
        let plbr = this.player.playbackRate();
        el.setAttribute('aria-expanded', 'true');
        let menu: Element | null = document.querySelector(
          '.vjs-control > .vjs-menu'
        );
        if (menu) {
          menu.setAttribute('class', 'vjs-menu vjs-lock-showing');
          let menuItems = menu.children[0].children;

          for (let index = 0; index < menuItems.length; index++) {
            const element = menuItems[index];
            element.setAttribute('tabindex', '0');
            element.addEventListener('blur', event => {
              let menu: Element | null = document.querySelector(
                '.vjs-control > .vjs-menu'
              );

              setTimeout(() => {
                if (menu) {
                  menu.contains(document.activeElement);
                  if (menu.contains(document.activeElement)) return false;
                }

                if (menu) menu.setAttribute('class', 'vjs-menu vjs-hidden');
              });
            });
          }
        }
        let firstMenuItem: Element | null = document.querySelector(
          '.vjs-control > .vjs-menu > .vjs-menu-content > .vjs-menu-item'
        );
        this.player.playbackRate(plbr);
      });
    

    let subtitlesDialog = document.querySelector('.vjs-text-track-settings > .vjs-modal-dialog-content');
    let closeButton = document.querySelector('.vjs-text-track-settings > .vjs-close-button');
    if(closeButton)
      closeButton.addEventListener('keydown', (e)=>{
        if ((e as KeyboardEvent).keyCode === 9 && !(e as KeyboardEvent).shiftKey) { //tab with no shift

          e.preventDefault();
          e.stopImmediatePropagation();
          if(subtitlesDialog){
            let firstEl = document.querySelector('.vjs-text-track-settings > .vjs-modal-dialog-content select');
            if(firstEl)
              (firstEl as HTMLElement).focus();
          }
        }
      });

    let elCaptionQuery: Element = document
      .querySelectorAll(
        '.vjs-subs-caps-button.vjs-menu-button.vjs-control button'
      )
      .item(0);

    if (elCaptionQuery) 
      elCaptionQuery.addEventListener('click', () => {
        let menu = document.querySelector(
          '.vjs-subs-caps-button.vjs-menu-button.vjs-control .vjs-menu'
        );
        if (menu) {

          let options = menu.children[0].children;
          menu.addEventListener('keydown', e => {

            if ((e as KeyboardEvent).keyCode === 9) //tab
              e.stopImmediatePropagation();
            else if (
              (e as KeyboardEvent).keyCode === 32 || // space
              (e as KeyboardEvent).keyCode === 13 // enter
            ) 
              setTimeout(() => {
                if (
                  (e.target as HTMLElement).className.indexOf(
                    'vjs-texttrack-settings'
                  ) > -1
                )
                  return;
                (elCaptionQuery as HTMLElement).click();
                (e.target as HTMLElement).focus();
              });
            
          });
          for (let index = 0; index < options.length; index++) {
            let option = options[index];
            option.setAttribute('tabindex', '0');
          }
        }
      });
    

    // adding scape button features back to control buttons. Latest update of videjs took this behavior off
    let controlButtons = document.querySelectorAll('.vjs-control, .vjs-subs-caps-button, .vjs-playback-rate, .vjs-progress-holder');
    for (let index = 0; index < controlButtons.length; index++) {
      const controlButton = controlButtons[index];
      if (controlButton) controlButton.addEventListener('keydown', (e)=>{
        if((e as KeyboardEvent).code == 'Escape')
          this.closeParentModal();
        else if((e as KeyboardEvent).keyCode == 27)
          this.closeParentModal();
        
      });
    }
  }

  unMuteVideoIfUserHasNotInteracted(
    isPlayerMuted: boolean,
    hasUserMuted: boolean
  ) {
    // If the video is muted, and the user has not explicitly
    // set their player to be muted. Lets un-mute the video.
    if (isPlayerMuted && hasUserMuted !== false) 
      this.player.muted(false);
    
  }

  handleSavedData(savedData: any) {
    if (this.saveVideoData && savedData && savedData.currentTime) {
      this.player.currentTime(savedData.currentTime);
      this.currentTime = savedData.currentTime;
    }
  }

  parseTextTracks(tracks: videojs.TextTrackOptions[]) {
    if (tracks)
      tracks.map((track: videojs.TextTrackOptions) => {
        const remoteTrack = this.player.addRemoteTextTrack(track, false);
        this.handleRemoteTrack(remoteTrack);
      });
  }

  handleRemoteTrack(remoteTrack: HTMLTrackElement) {
    if (remoteTrack)
      remoteTrack.addEventListener('load', this.onRemoteTrackLoad.bind(this, remoteTrack));
  }

  onRemoteTrackLoad(remoteTrack: HTMLTrackElement){
    if(!this.player) return;
    try{
      this.afterTrackLoads(remoteTrack);
      this.forceTrackLoad(this.player.textTracks(),AppState.hasNeverChangedCaptionsSettings);
    } catch (err) {
      // Ignore error
      // eslint-disable-next-line no-console
      console.log('Video JS Supressed Error:', err);
    }
  }

  afterTrackLoads(remoteTrack: HTMLTrackElement) {
    this.handleCaptionTrack(remoteTrack.track);

    this.textTrackSettings.on('click', this.handleCaptionSettingsMenu);
    // SETTING SHOW CC STATES
    this.player.textTracks().onchange = this.handleClosedCaptionsChange;
    // SETTING CC SETTINGS
    this.setTextTrackSettings(UserConfState.getTextTrackSettingsOptions);
  }

  handlePlaybackChange() {
    let hasChanged = UserConfState.getSpeedRatio !== this.player.playbackRate();

    UserConfState.changeSpeedRatio(this.player.playbackRate());
    this.player.defaultPlaybackRate(this.player.playbackRate());

    if(hasChanged)
      this.$analytics.trackEvent(BTBA.changeVideoSetting,{
        'Video SRC' : `${this.player.src()}`,
        'Video Duration' : this.duration,
        'Changed Option' : 'playbackspeed',
        'Selected Value' : this.player.playbackRate()
      } as ChangeVideoSettingProperties);
    
  }

  handlePlayerMuted() {
    UserConfState.setVolume(this.player.volume());
    this.adjustMuteState(this.player.muted(), UserConfState.getAudio);
  }

  adjustMuteState(isPlayerMuted: boolean, isApplicationAudioOn: boolean) {
    if (!isPlayerMuted && !isApplicationAudioOn) 
      UserConfState.changeAudio(true);
    else if (isPlayerMuted && isApplicationAudioOn) 
      UserConfState.changeAudio(false);
    
  }

  handleCaptionTrack(track: TextTrack) {
    if (track.kind === 'captions') {
      const the_track = { lang: track.language, cues: [] as object[] };
      if (track.cues) 
        for (var x = 0; x < track.cues.length; x++) {
          let vttTackCue = (track.cues[x] as VTTCue);
          const cue: object = {
            start: vttTackCue.startTime,
            end: vttTackCue.endTime,
            text: vttTackCue.text
          };
          the_track.cues.push(cue);
          this.transcriptions.push(cue);
        }
      
      this.avaliableTranscriptions[the_track.lang] = the_track;
      this.avaliableTranscriptions = Object.assign(
        {},
        this.avaliableTranscriptions
      );
      this.setTranscription(the_track.lang);
      // SET CC APPEARANCE
      if (UserConfState.getShowCaptions)
        this.setInitialShowCaptionsMode(UserConfState.getShowCaptions);
    }
  }

  setInitialShowCaptionsMode(showCaptions: boolean) {
    if (showCaptions && this.player.textTracks().length > 0)
      this.player.textTracks()[0].mode = UserConfState.getShowCaptionsMode;
  }

  setTranscription(lang: string) {
    this.currentTranscription = !this.currentTranscription
      ? lang
      : this.currentTranscription;
  }

  handleTimeUpdate() {
    this.currentTime = this.player.currentTime();
    if (this.$refs.scrollList && this.autoscroll) {
      const current = this.$refs.scrollList.getElementsByClassName(
        'current'
      )[0];
      this.addSmoothScrollBehavior(current);
    }
    this.duration = this.player.duration();
    this.finished = this.currentTime >= this.duration;
    if(this.finished)
      this.handleFinishVideo();
    const watched95percent =
      this.player.currentTime() > this.player.duration() * 0.95;
    this.emitFinishingEvents(watched95percent);

    if (this.saveVideoData) 
      this.performSaveData(this.currentTime, this.lastSavedTime);
    
  }

  performSaveData(currentTime: number, lastSavedTime: number){
    if (currentTime > lastSavedTime + 43) {
      this.lastSavedTime = this.currentTime;
      //every 43 seconds
      this.saveData({
        currentTime: this.currentTime,
        completed: this.hasCompleted
      });
    }
  }

  handleFinishVideo(){
    setTimeout(()=>{
      if(this.$refs.closeVideo)
        this.$refs.closeVideo.focus();
      this.emitRefreshed();
      this.handleVideoFullscreen();

    },300);

  }

  emitFinishingEvents(hasWatchedUntilTheEnd: boolean) {
    if (hasWatchedUntilTheEnd && !this.completeSaved) {
      this.emitCompleted();
      if (this.locksSlide) this.unlock();
      if (this.saveVideoData) this.saveData({ completed: true });
      this.completeSaved = true;
      this.hasCompleted = true;
      this.$analytics.trackEvent(BTBA.finishedVideo,{
        'Video SRC' : `${this.player.src()}`,
        'Video Duration' : this.duration,
        'Video Watched Time': this.sessionTimer.getTotalTimeValues().seconds,
        'Video Caption': this.currentCaption,
        'Video Playback Speed': this.player.playbackRate(),
        'Transcript Opened':  this.showVideoTranscript
      } as FinishedVideoProperties);
      this.sessionTimer.reset();
    }
  }

  addSmoothScrollBehavior(current: any) {
    if (current) 
      current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    
  }

  toggleTranscriptions() {
    UserConfState.toggleShowVideoTranscript();
  }

  forceTrackLoad(tracks: TextTrackList, forceShowCaptions: boolean = false) {
    this.loadTranscript = false;
    for (var i = 0; i < tracks.length; i++) {
      var track = tracks[i];
      if (
        track.kind === 'captions' &&
        track.language === this.currentTranscription &&
        UserConfState.getShowCaptions
      ) 
        track.mode = 'showing';
      else 
        track.mode = 'hidden';
      
      if(forceShowCaptions && Theme.showCaptionsByDefault)
        tracks[0].mode = 'showing';
      
    }
  }

  toggleAutoScroll() {
    UserConfState.toggleAutoScroll();
    this.autoscroll = UserConfState.getAutomaticScroll;
  }
  toggleTranscriptionsOptions() {
    this.showTranscriptOptions = !this.showTranscriptOptions;
  }
  handleVideoPlay(){ // TODO: not easy to cover on tests
    if(!this.sessionTimer.isRunning())
      this.sessionTimer.start();
    
  }

  handleVideoFullscreen(){
    let controls = document.querySelector('.endFeedback');
    if(controls) document.querySelector('.video-js')?.appendChild(controls);
  }

  isTrascriptAvaliable(options:any):boolean{
    return options.sources && options.sources.length > 0 && options.tracks && options.tracks.length > 0;
  }

  get showVideoTranscript():boolean {
    return this.isTrascriptAvaliable(this.options) && UserConfState.showVideoTranscript;
  }
  get currentCaption() : string {
    let result = 'none';
    (this.player.textTracks() as any).tracks_.map((track : TextTrack) =>{
      if(track.mode == 'showing')
        result = track.language.toLowerCase();
    });
    return result;
  }
}
